import React from "react";
import { included, notIncluded, tripFacts } from "../mock";
import Reveal from "./Reveal";
import { Check, X, Plane, Bed, Train, MapPin, Users, Calendar } from "lucide-react";

const Fact = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 p-4 bg-[#fdfaf2] border border-[#1a3a2e]/10 rounded-sm">
    <div className="w-10 h-10 rounded-sm bg-[#1a3a2e] flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-[#e9c987]" strokeWidth={1.5} />
    </div>
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#3b463f]/60">{label}</div>
      <div className="font-display text-[#1a3a2e] text-lg leading-tight">{value}</div>
    </div>
  </div>
);

const Pricing = () => {
  return (
    <section id="pricing" className="section bg-paper">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="text-center mb-14">
            <span className="font-script text-[#c9a55c] text-3xl block mb-2">investment</span>
            <h2 className="font-display text-[#1a3a2e] text-4xl md:text-5xl mb-3">
              Pricing &amp; <em className="italic text-[#c9a55c]">Details</em>
            </h2>
            <p className="text-[#3b463f]/80 max-w-xl mx-auto font-light">
              Everything you need to know, all in one place.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
            <Fact icon={Calendar} label="Duration" value={tripFacts.duration} />
            <Fact icon={Plane} label="Travel Dates" value={tripFacts.dates} />
            <Fact icon={Users} label="Group Size" value={tripFacts.groupSize} />
            <Fact icon={MapPin} label="Begins" value={tripFacts.startCity} />
            <Fact icon={MapPin} label="Ends" value={tripFacts.endCity} />
            <Fact icon={Bed} label="Lodging" value="9 Nights, 3–4★" />
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Price Card */}
          <Reveal>
            <div className="bg-[#1a3a2e] text-[#faf5ec] p-10 rounded-sm relative overflow-hidden h-full">
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#c9a55c]/15 blur-2xl" />
              <div className="relative">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#e9c987]">Trip Cost</span>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-6xl text-[#faf5ec]">{tripFacts.price}</span>
                  <span className="text-[#faf5ec]/60 text-sm">/ person</span>
                </div>
                <p className="text-[#faf5ec]/70 text-sm mt-2 font-light">
                  Based on double occupancy. Airfare not included.
                </p>
                <div className="my-8 h-px bg-[#faf5ec]/15" />
                <ul className="space-y-3 text-sm font-light">
                  <li className="flex gap-2">
                    <span className="text-[#e9c987] mt-1 shrink-0">•</span>
                    <span>
                      Registration Fee: <strong className="text-[#e9c987] font-medium">{tripFacts.deposit}</strong> — non-refundable & <em>not</em> applied to the trip total
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[#e9c987] mt-1 shrink-0">•</span>
                    <span>Full $2,695 trip balance due by August 29, 2026</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[#e9c987] mt-1 shrink-0">•</span>
                    <span>Estimate ~$1,000 round-trip multi-city airfare</span>
                  </li>
                </ul>
                <a href="#reserve" className="btn-outline mt-8 w-full justify-center">
                  Reserve Your Spot
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="bg-[#fdfaf2] border border-[#1a3a2e]/10 p-8 rounded-sm h-full">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-full bg-[#1a3a2e] flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#e9c987]" strokeWidth={2.5} />
                </div>
                <h3 className="font-display text-2xl text-[#1a3a2e]">What&rsquo;s Included</h3>
              </div>
              <ul className="space-y-3">
                {included.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#3b463f] text-[15px] font-light">
                    <Check className="w-4 h-4 text-[#c9a55c] mt-1 shrink-0" strokeWidth={2} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={220}>
            <div className="bg-[#fdfaf2] border border-[#1a3a2e]/10 p-8 rounded-sm h-full">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-full bg-[#8b3a3a] flex items-center justify-center">
                  <X className="w-4 h-4 text-[#faf5ec]" strokeWidth={2.5} />
                </div>
                <h3 className="font-display text-2xl text-[#1a3a2e]">Not Included</h3>
              </div>
              <ul className="space-y-3">
                {notIncluded.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#3b463f] text-[15px] font-light">
                    <X className="w-4 h-4 text-[#8b3a3a] mt-1 shrink-0" strokeWidth={2} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <div className="mt-12 bg-[#fdfaf2] border border-[#c9a55c]/30 rounded-sm p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <Train className="w-10 h-10 text-[#c9a55c] shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <h4 className="font-display text-[#1a3a2e] text-xl mb-1">Airfare Tip from Sophia</h4>
              <p className="text-[#3b463f]/85 text-sm font-light leading-relaxed">
                Multi-city tickets (LAX → LHR … CDG → LAX) are often the best value for this itinerary. I&rsquo;ll share booking tips so you can grab the best fares — booking early always pays off.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
